<script setup>
/**
 * Workspace office hours.
 *
 * The account-level twin of an inbox's business hours: same weekly grid, same
 * time-slot helpers, one schedule for the whole workspace. Inboxes that set
 * their own hours keep overriding this.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import OfficeHoursAPI from 'dashboard/api/officeHours';
import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import BusinessDay from 'dashboard/routes/dashboard/settings/inbox/components/BusinessDay.vue';
import {
  timeSlotParse,
  timeSlotTransform,
  defaultTimeSlot,
  timeZoneOptions,
} from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour';

const { t } = useI18n();

const DAY_NAMES = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const isEnabled = ref(false);
const timezone = ref('Etc/UTC');
const timeSlots = ref([...defaultTimeSlot]);
const isSaving = ref(false);
const isDirty = ref(false);

const timezones = timeZoneOptions();

const hasError = computed(() => {
  if (!isEnabled.value) return false;
  return timeSlots.value.some(slot => slot.from && !slot.valid);
});

const setDefaults = ({ enabled, timezone: zone, office_hours: schedule }) => {
  isEnabled.value = Boolean(enabled);
  timezone.value = zone;
  const parsed = timeSlotParse(schedule ?? []);
  timeSlots.value = parsed.length ? parsed : [...defaultTimeSlot];
  isDirty.value = false;
};

onMounted(async () => {
  const { data } = await OfficeHoursAPI.get();
  setDefaults(data);
});

const onSlotUpdate = (day, slotData) => {
  timeSlots.value = timeSlots.value.map(slot =>
    slot.day === day ? slotData : slot
  );
  isDirty.value = true;
};

const onSave = async () => {
  isSaving.value = true;
  try {
    const { data } = await OfficeHoursAPI.update({
      enabled: isEnabled.value,
      timezone: timezone.value,
      office_hours: timeSlotTransform(timeSlots.value),
    });
    setDefaults(data);
    useAlert(t('OFFICE_HOURS.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error.response?.data?.message ?? t('OFFICE_HOURS.API.ERROR_MESSAGE')
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SettingsPageShell
    :title="$t('OFFICE_HOURS.TITLE')"
    :description="$t('OFFICE_HOURS.DESCRIPTION')"
    show-save
    :is-dirty="isDirty && !hasError"
    :is-saving="isSaving"
    @save="onSave"
  >
    <SettingsToggleSection
      v-model="isEnabled"
      :header="$t('OFFICE_HOURS.TOGGLE.HEADER')"
      :description="$t('OFFICE_HOURS.TOGGLE.DESCRIPTION')"
      @update:model-value="isDirty = true"
    />

    <template v-if="isEnabled">
      <SettingsFieldSection :label="$t('OFFICE_HOURS.TIMEZONE_LABEL')">
        <ComboBox
          v-model="timezone"
          :options="timezones"
          :placeholder="$t('OFFICE_HOURS.TIMEZONE_PLACEHOLDER')"
          class="[&>div>button]:!bg-n-alpha-black2"
          @update:model-value="isDirty = true"
        />
      </SettingsFieldSection>

      <h3 class="text-heading-2 text-n-slate-12">
        {{ $t('OFFICE_HOURS.WEEKLY_TITLE') }}
      </h3>

      <div
        class="w-full overflow-hidden border rounded-xl border-n-weak bg-n-solid-1"
      >
        <table class="min-w-full table-auto">
          <thead>
            <tr class="border-b bg-n-alpha-1 border-n-weak">
              <th
                class="py-3 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-start text-label-small uppercase tracking-wide text-n-slate-11"
              >
                {{ $t('OFFICE_HOURS.DAY') }}
              </th>
              <th
                class="py-3 ltr:pr-3 rtl:pl-3 text-start text-label-small uppercase tracking-wide text-n-slate-11"
              >
                {{ $t('OFFICE_HOURS.AVAILABILITY') }}
              </th>
              <th
                class="py-3 ltr:pr-3 rtl:pl-3 text-start text-label-small uppercase tracking-wide text-n-slate-11"
              >
                {{ $t('OFFICE_HOURS.HOURS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <BusinessDay
              v-for="timeSlot in timeSlots"
              :key="timeSlot.day"
              :day-name="DAY_NAMES[timeSlot.day]"
              :time-slot="timeSlot"
              @update="data => onSlotUpdate(timeSlot.day, data)"
            />
          </tbody>
        </table>
      </div>
    </template>
  </SettingsPageShell>
</template>
